import React, { useEffect } from 'react'
import styles from './styles.module.css'
import { clearPrice, containsPrice, replacePrice } from '../../utils/text'
import { useNamedPrices } from '../NamedPrices'
import MyLoader from '../Utils/MyLoader'
import {
  types,
  resolveVariant,
  resolveTextRender,
  applyContentStyling,
} from './render'

// CLIENT <Text>. The variant→tag/class map, the trademark (®) + highlight content
// styling and the element-building live in the shared, pure ./render module
// (byte-parity twin of <TextServer>, enforced by __tests__/Server.test.js). This
// wrapper keeps ONLY the client-only concerns: the named-price substitution
// (containsPrice/clearPrice/replacePrice + the fetch effect) and the MyLoader
// price-skeleton overlay. See ./render for the single-<h1> `primary` contract.
//
// The named-price DATA now comes from the NamedPrices abstraction
// (useNamedPrices) — server-fetched region prices threaded through
// <NamedPricesProvider>, with a transitional Overmind fallback isolated inside
// that hook — instead of a direct render-time `state.namedPrices` Overmind read.

function Text(props) {
  const { children, type, className, color, highlight, highlightSx, highlightExactMatch, primary } = props

  let updatedContent = children

  const { namedPrices, namedPricesFetched, ensureRequested } = useNamedPrices()

  const typeKey = String(type).toLowerCase()

  if (types.indexOf(typeKey) < 0) {
    console.error(`Text: The type you have selected does not exist: "${type}". Select one of ${types}`)
  }

  // on load, if text contains price placeholders, request named prices (no-op on
  // the server-props path where they are already present; only the fallback path
  // triggers a client fetch — same condition as before).
  useEffect(() => {
    if (containsPrice(children)) {
      ensureRequested()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let isLoading = false

  if (containsPrice(children)) {
    isLoading = !namedPricesFetched
    updatedContent = !namedPricesFetched ? clearPrice(updatedContent) : replacePrice(updatedContent, namedPrices)
  }

  updatedContent = applyContentStyling(updatedContent, { highlight, highlightSx, highlightExactMatch })

  const { Tag, className: elementClassName, style } = resolveTextRender({ type, className, color, primary })
  const element = (
    <Tag className={elementClassName} style={style}>
      {updatedContent}
    </Tag>
  )

  const variant = resolveVariant(typeKey)
  return variant.hideable && isLoading ? withLoader({ type, className, color, primary }, updatedContent, element) : element
}

// Render the price-skeleton overlay ONLY while loading (task #76). Previously
// this wrapped EVERY loader-capable Text in a relative <div> and emitted a full
// (hidden) loader SVG unconditionally — ~340 duplicate loaders on /us/shop. Now:
//   - not loading  -> return the plain text element (no wrapper, no loader DOM).
//   - loading      -> wrap in the relative TextWrapper and overlay one CSS
//                     skeleton (MyLoader), whose text is hidden via the `hide`
//                     visibility class.
// isLoading is deterministic server==client (it derives from SSR-hydrated
// namedPricesFetched), so the conditional wrapper is hydration-safe.
const withLoader = (renderProps, content, element) => {
  const { Tag, className: hiddenClassName, style } = resolveTextRender({ ...renderProps, hide: true })

  return (
    <div className={styles.textWrapper}>
      <Tag className={hiddenClassName} style={style}>
        {content}
      </Tag>
      <MyLoader isLoading={true} />
    </div>
  )
}

export default Text
